import type * as SentryModule from '@sentry/react';
import React from 'react';

import { config } from '@/config';
import * as Sentry from '@/sentry';

// Note: there have been some crashes coming from the internals of the sentry
// replay module, and we haven't used the replays, so disable them for now.
export const sentryReplay: any = false;

/**
 * Client transaction sample rate for performance traces.
 *
 * FOOD-1403: reduced from 1% to 0.5% to cut span volume (~120k/day at 1%).
 * Selective http.client span filtering was not used: @sentry/react 7.29 had no
 * ignoreSpans/beforeSendSpan, sibling apps only tune tracesSampleRate, and
 * dropping http.client via beforeSendTransaction would remove API latency from
 * otherwise useful navigation/long-task traces. Error/crash reporting is unchanged.
 */
export const TRACES_SAMPLE_RATE = 0.005;

/** Default integrations Sentry added after v7, left out so no new data is sent. */
const NEW_DEFAULT_INTEGRATIONS = ['CultureContext', 'ConversationId'];

/**
 * Triggers a Sentry Replay recording session while the component is mounted.
 */
export const useSentryReplay = () => {
  React.useEffect(() => {
    if (!sentryReplay) {
      return;
    }
    sentryReplay.start();
    return () => {
      sentryReplay.stop();
    };
  }, []);
};

/**
 * Page load, navigation and API request spans, plus the LCP and CLS web vitals. Everything else the
 * tracing integration can record is off: long tasks, long animation frames, INP, element timing and one
 * span per loaded script, stylesheet and image. Those made up most of the spans in a page-load trace and
 * nobody reads them.
 */
export const BROWSER_TRACING_OPTIONS = {
  // Tracing extras added after v7, which would add spans and attributes.
  webVitals: { ignore: ['inp'] },
  enableHTTPTimings: false,
  instrumentBfcacheRestore: false,
  linkPreviousTrace: 'off',
  enableLongTask: false,
  enableLongAnimationFrame: false,
  enableInp: false,
  enableElementTiming: false,
  ignoreResourceSpans: ['resource.script', 'resource.css', 'resource.img', 'resource.other', 'resource.link'],
} satisfies Parameters<typeof SentryModule.browserTracingIntegration>[0];

/** Sentry options, kept separate from init so tests can check what gets sent. Takes the lazily loaded SDK. */
export const sentryOptions = (sdk: typeof SentryModule): SentryModule.BrowserOptions => ({
  dsn: config.SENTRY_DSN,
  environment: config.IS_PREVIEW ? 'preview' : config.ENV,
  release: config.COMMIT_SHA,

  ignoreErrors: [
    // Outlook's Safe Link checker throws these errors:
    // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/16
    'Non-Error promise rejection captured with value: Object Not Found Matching',
  ],

  // Keep v7's default integrations only. v11 adds CultureContext (locale and
  // time zone on every event) and ConversationId (AI SDK spans), neither sent before.
  integrations: defaults =>
    [
      ...defaults.filter(
        integration => !NEW_DEFAULT_INTEGRATIONS.includes(integration.name) && integration.name !== 'BrowserSession',
      ),
      // v11 creates one session per page load; v7 started a new one on every navigation.
      sdk.browserSessionIntegration({ lifecycle: 'route' }),
      sdk.browserTracingIntegration(BROWSER_TRACING_OPTIONS),
      sentryReplay,
    ].filter(Boolean),

  tracesSampleRate: TRACES_SAMPLE_RATE,
  // v11 streams spans by default; send whole transactions as v7 did.
  traceLifecycle: 'static',
  // v11 attaches synthetic stack traces to messages and non-Error captures by default.
  attachStacktrace: false,
  // v11 appends the request hostname to fetch error messages by default.
  enhanceFetchErrorMessages: false,

  // Collect as little as the SDK allows. v11 removed sendDefaultPii and
  // defaults every one of these to on.
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: false,
    httpBodies: [],
    urlQueryParams: false,
    graphQL: { document: false, variables: false },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    queues: false,
    stackFrameVariables: false,
    frameContextLines: 0,
  },

  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.0,

  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 0.0,
});

/**
 * How long after the page has loaded before the Sentry SDK is fetched. Errors in that window are
 * queued by the `sentry` front. Loading it with the entry chunk competed with first paint: the SDK
 * is about 148 kB and most of it does not run during startup.
 */
export const SENTRY_LOAD_DELAY_MS = 5000;

const scheduleSentryLoad = (load: () => void) => {
  const start = () => {
    window.setTimeout(load, SENTRY_LOAD_DELAY_MS);
  };
  if (document.readyState === 'complete') {
    start();
    return;
  }
  window.addEventListener('load', start, { once: true });
};

/**
 * Loads and starts Sentry after the page load, so it does not compete with first paint. Until the SDK
 * is up, uncaught errors and unhandled rejections are buffered by the `sentry` front and sent once it
 * is; after that the SDK's own global handlers take over.
 */
export const initAnalytics = (loadSdk: () => Promise<typeof SentryModule> = () => import('@sentry/react')) => {
  if (config.IS_LOCAL) {
    Sentry.disableSentry();
    return Promise.resolve();
  }

  const onError = (event: ErrorEvent) => Sentry.captureException(event.error ?? event.message);
  const onRejection = (event: PromiseRejectionEvent) => Sentry.captureException(event.reason);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  const stopBuffering = () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };

  return new Promise<void>(resolve => {
    scheduleSentryLoad(() => {
      loadSdk().then(
        sdk => {
          stopBuffering();
          sdk.init(sentryOptions(sdk));
          Sentry.attachSentry(sdk);
          resolve();
        },
        error => {
          stopBuffering();
          Sentry.disableSentry();
          console.error('Sentry failed to load; errors will not be reported:', error);
          resolve();
        },
      );
    });
  });
};
